/* eslint-disable no-underscore-dangle */

import { JSX } from 'react';

import {
  DecoratorNode,
  NodeKey,
  SerializedElementNode,
  Spread,
  LexicalEditor,
} from 'lexical';

import { Image } from '../../plugins';
import { WysiwygConfigMode } from '../../WysiwygInput';

import {
  $createSingleImageNode,
  $isSingleImageNode,
} from './SingleImageNode.helpers';

type SerializedSingleImageNode = Spread<
  {
    alt: string;
    id: string;
    description?: string;
    mode?: WysiwygConfigMode;
    type: 'single-image';
    version: 1;
  },
  SerializedElementNode
>;

export class SingleImageNode extends DecoratorNode<JSX.Element> {
  alt: string;

  description: string | undefined;

  id: string;

  mode: WysiwygConfigMode | undefined;

  constructor(
    alt: string,
    id: string,
    description?: string,
    mode?: WysiwygConfigMode,
    key?: NodeKey,
  ) {
    super(key);
    this.alt = alt;
    this.id = id;
    this.description = description;
    this.mode = mode;
  }

  getId(): string {
    return this.id;
  }

  decorate(editor: LexicalEditor): JSX.Element {
    return (
      <Image
        alt={this.alt}
        id={this.id}
        description={this.description}
        mode={this.mode}
        editable={editor._editable}
      />
    );
  }

  static getType(): string {
    return 'single-image';
  }

  static clone(node: SingleImageNode): SingleImageNode {
    return new SingleImageNode(
      node.alt,
      node.id,
      node.description,
      node.mode,
      node.__key,
    );
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('span');
    dom.setAttribute('data-id', this.id);
    return dom;
  }

  updateDOM(prevNode: SingleImageNode, dom: HTMLElement): boolean {
    if (!$isSingleImageNode(prevNode)) return true;

    const propertiesChanged =
      this.id !== prevNode.id ||
      this.alt !== prevNode.alt ||
      this.description !== prevNode.description;

    if (this.id !== prevNode.id) {
      dom.dataset.id = this.id;
    }

    return propertiesChanged;
  }

  exportJSON(): SerializedSingleImageNode {
    return {
      type: 'single-image',
      version: 1,
      alt: this.alt,
      id: this.id,
      description: this.description,
      mode: this.mode,
      children: [],
      direction: null,
      format: '',
      indent: 0,
    };
  }

  static importJSON(
    serializedNode: SerializedSingleImageNode,
  ): SingleImageNode {
    return $createSingleImageNode(
      serializedNode.alt,
      serializedNode.id,
      serializedNode.description,
      serializedNode.mode,
    );
  }

  getTextContent(): string {
    return this.description || '';
  }
}
